import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getPromoCode } from "@/hooks/usePromo";
import { useAuth } from "@/contexts/AuthContext";

// Product ID mapping for subscription tiers
// Note: the legacy Standard tier has been removed. Pro is the entry paid tier
// (starts with a 7-day free trial); Business is unlimited.
export const SUBSCRIPTION_TIERS = {
  pro: {
    productId: "prod_V38uDXDDImsgJp",
    priceId: "price_1U32cl1EmJwsr1NRi2UxEeMi", // $59/mo list price
    // Ad-pathway lifetime deal, only granted when checkout validates the promo code.
    promoPriceId: "price_1U32cy1EmJwsr1NRqmLrmKH7", // $29/mo lifetime deal
    role: "pro" as const,
  },
  business: {
    productId: "prod_V38y7H8Loej7sA",
    priceId: "price_1U32g81EmJwsr1NROrYIstUE", // $100/mo
    role: "business" as const,
  },
};

interface SubscriptionStatus {
  subscribed: boolean;
  productId: string | null;
  subscriptionEnd: string | null;
  role: "trial" | "expired" | "pro" | "business" | "membership" | "admin" | null;
  /** Monthly price in cents after any promotion code. */
  amountMonthly?: number | null;
  discountCode?: string | null;
  percentOff?: number | null;
  /** Fixed discount in cents. */
  amountOff?: number | null;
}

/** e.g. "$47.20/month (BROKERAGE20, 20% off)" or null when no discount. */
export const formatDiscountedPrice = (
  s: Pick<SubscriptionStatus, "amountMonthly" | "discountCode" | "percentOff" | "amountOff">,
  suffix = "",
): string | null => {
  if (s.amountMonthly == null || (!s.percentOff && !s.amountOff)) return null;
  const dollars = s.amountMonthly / 100;
  const amt = Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
  const off = s.percentOff ? `${s.percentOff}% off` : `$${((s.amountOff ?? 0) / 100).toFixed(2).replace(/\.00$/, "")} off`;
  return `${amt}/month${suffix} (${s.discountCode ? `${s.discountCode}, ` : ""}${off})`;
};

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export const useSubscription = () => {
  const { session, initializing } = useAuth();
  const [status, setStatus] = useState<SubscriptionStatus>({
    subscribed: false,
    productId: null,
    subscriptionEnd: null,
    role: null,
  });
  const [loading, setLoading] = useState(true);
  const lastCheckedRef = useRef<number>(0);
  const inFlightRef = useRef<boolean>(false);
  const statusRef = useRef(status);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const checkSubscription = useCallback(async (force = false) => {
    // Skip if recently checked (within cache TTL) unless forced
    const now = Date.now();
    if (!force && now - lastCheckedRef.current < CACHE_TTL_MS) {
      return;
    }

    // Prevent concurrent calls
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      setLoading(true);
      if (!session) {
        setStatus({
          subscribed: false,
          productId: null,
          subscriptionEnd: null,
          role: null,
        });
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke("check-subscription", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error("Error checking subscription:", error);
        throw error;
      }

      lastCheckedRef.current = Date.now();

      // Role is now synced server-side by check-subscription edge function
      setStatus({
        subscribed: data.subscribed,
        productId: data.product_id,
        subscriptionEnd: data.subscription_end,
        role: data.role || "expired",
        amountMonthly: data.amount_monthly ?? null,
        discountCode: data.discount_code ?? null,
        percentOff: data.percent_off ?? null,
        amountOff: data.amount_off ?? null,
      });
    } catch (error) {
      console.error("Failed to check subscription:", error);
      // A temporary transport failure must never downgrade a signed-in user
      // or replace a previously verified subscription with an expired state.
      // The database role remains authoritative until a successful check.
      setStatus(statusRef.current);
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  }, [session]);

  const createCheckout = async (priceId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error("Authentication required", { description: "Please log in to subscribe" });
        return null;
      }

      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId, promoCode: getPromoCode() ?? undefined },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      return data.url;
    } catch (error) {
      console.error("Failed to create checkout:", error);
      toast.error("Error", { description: "Failed to create checkout session" });
      return null;
    }
  };

  const openCustomerPortal = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error("Authentication required", { description: "Please log in to manage your subscription" });
        return;
      }

      const { data, error } = await supabase.functions.invoke("customer-portal", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      window.open(data.url, "_blank");
    } catch (error) {
      console.error("Failed to open customer portal:", error);
      toast.error("Error", { description: "Failed to open customer portal" });
    }
  };

  useEffect(() => {
    if (initializing) return;

    checkSubscription(true); // Force on mount

    // Check subscription status when tab becomes visible (throttled by cache TTL)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkSubscription(); // Will skip if within 5-min cache
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id, initializing]);

  return {
    ...status,
    loading,
    checkSubscription: () => checkSubscription(true),
    createCheckout,
    openCustomerPortal,
  };
};
